import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { memberInsights, gymSettings } from '../services/api';
import SmsDialog from '../components/Operations/SmsDialog';

/*
 * Sócios inativos — o ecrã que avisa o dono de quem está a caminho de se ir embora.
 *
 * Duas regras que este ecrã não negoceia:
 *  1. O limiar («inativo depois de N semanas») é a definição do GINÁSIO, não uma regra nossa. Vem do servidor
 *     (`GymSettings.InactiveAfterWeeks`, 4 semanas por omissão) e está escrito à frente do número.
 *  2. Quem NUNCA treinou não é quem parou. São listas separadas, com contagens separadas: juntá-las daria
 *     «inativo há 0 dias» a quem se registou ontem e esconderia a diferença entre um sócio a fugir e uma
 *     conta nova por começar.
 *
 * Os sinais vêm do servidor (treinos concluídos e check-ins/presenças) e o ecrã diz quais foram usados.
 * A mensagem abre o MESMO diálogo de SMS do balcão — não há canal novo.
 */

const THRESHOLD_OPTIONS = [1, 2, 3, 4, 6, 8, 12, 16, 26, 52];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-PT') : '—');

/** «há N dias/semanas/meses» — a leitura que o dono faz no telemóvel, sem contas. */
export const sinceText = (days) => {
  if (days === null || days === undefined) return '—';
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  if (days < 14) return `há ${days} dias`;
  if (days < 60) return `há ${Math.floor(days / 7)} semanas`;
  if (days < 365) return `há ${Math.floor(days / 30)} meses`;
  return `há ${Math.floor(days / 365)} ano(s)`;
};

const SIGNAL_LABEL = {
  workout: 'Último treino',
  checkin: 'Última presença em aula',
  entry: 'Última entrada no ginásio',
};

const messageFor = (name) => {
  const first = (name || '').trim().split(/\s+/)[0];
  return `Olá${first ? `, ${first}` : ''}! Sentimos a sua falta no treino. Quer que lhe preparemos uma sessão?`;
};

export default function AdminInactiveMembersPage() {
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('inactive');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [target, setTarget] = useState(null);   // { member, message } — abre o SmsDialog pré-preenchido

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([memberInsights.inactive(), memberInsights.weekly()]);
      setData(a.data);
      setWeekly(b.data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const threshold = data?.thresholdWeeks ?? 4;

  const changeThreshold = async (weeks) => {
    setSaving(true);
    try {
      await gymSettings.update({ inactiveAfterWeeks: weeks });
      await load();
      toast.success(`A partir de agora, inativo é quem não treina há ${weeks} semanas.`);
    } catch {
      toast.error('Não foi possível guardar o limiar do ginásio.');
    } finally {
      setSaving(false);
    }
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const r = await memberInsights.emailWeekly();
      toast.success(`Relatório enviado para ${r.data?.to || 'o seu email'}.`);
    } catch (e) {
      const d = e?.response?.data || {};
      toast.error(d.error === 'email-disabled'
        ? 'O envio por email não está configurado neste ginásio.'
        : 'Não foi possível enviar o relatório.');
    } finally {
      setSending(false);
    }
  };

  const openMessage = (member) => setTarget({
    member: { id: member.userId, name: member.name, phone: member.phone, email: member.email },
    message: messageFor(member.name),
  });

  const stopped = data?.stopped || [];
  const never = data?.neverTrained || [];
  const totalFlagged = (data?.inactiveCount || 0) + (data?.neverTrainedCount || 0);

  const signalsText = useMemo(() => (
    data?.usesCheckInSignals
      ? 'Sinais usados: treinos concluídos e check-ins (entradas e presenças em aulas). Conta o mais recente.'
      : 'Sinais usados: apenas treinos concluídos — este ginásio ainda não tem check-ins registados.'
  ), [data]);

  if (loading && !data) return <div style={S.wrap}><p style={S.dim}>A carregar sócios…</p></div>;
  if (error || !data) {
    return (
      <div style={S.wrap}>
        <div style={S.card}>
          <strong style={S.h3}>Não foi possível carregar os sócios inativos.</strong>
          <p style={S.dim}>A lista não foi carregada — isto não quer dizer que ninguém parou de treinar.</p>
          <button type="button" style={S.primary} onClick={load}>Tentar novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <header style={S.header}>
        <div>
          <h1 style={S.h1}>Sócios inativos</h1>
          <p style={S.dim}>
            Quem não treina está a caminho de se ir embora. Esta é a fila de trabalho, do mais antigo para o mais recente.
          </p>
        </div>
        <div style={S.threshold}>
          <label htmlFor="threshold" style={S.label}>Inativo depois de</label>
          <select
            id="threshold"
            data-testid="threshold"
            style={S.select}
            value={threshold}
            disabled={saving}
            onChange={(e) => changeThreshold(Number(e.target.value))}
          >
            {(THRESHOLD_OPTIONS.includes(threshold)
              ? THRESHOLD_OPTIONS
              : [...THRESHOLD_OPTIONS, threshold].sort((a, b) => a - b)
            ).map((w) => <option key={w} value={w}>{w} {w === 1 ? 'semana' : 'semanas'}</option>)}
          </select>
        </div>
      </header>

      <p style={S.rule} data-testid="threshold-rule">
        Consideramos inativo quem não treina há <strong>{threshold} {threshold === 1 ? 'semana' : 'semanas'}</strong>.
        {saving ? ' A guardar…' : ''}
      </p>
      <p style={S.signals}>{signalsText}</p>

      <div style={S.stats}>
        <Stat label="Sócios ativos" value={data.totalActiveMembers} />
        <Stat label="Pararam de treinar" value={data.inactiveCount} tone={data.inactiveCount > 0 ? 'warn' : undefined} />
        <Stat label="Nunca treinaram" value={data.neverTrainedCount} tone={data.neverTrainedCount > 0 ? 'warn' : undefined} />
        <Stat label="Sem treinar (total)" value={totalFlagged} hint={`de ${data.totalActiveMembers}`} />
      </div>

      <div style={S.tabs} role="tablist" aria-label="Sócios inativos e relatório">
        <button
          type="button" role="tab" aria-selected={tab === 'inactive'}
          style={{ ...S.tab, ...(tab === 'inactive' ? S.tabOn : {}) }}
          onClick={() => setTab('inactive')}
        >
          Sócios inativos
        </button>
        <button
          type="button" role="tab" aria-selected={tab === 'week'}
          style={{ ...S.tab, ...(tab === 'week' ? S.tabOn : {}) }}
          onClick={() => setTab('week')}
        >
          Relatório da semana
        </button>
      </div>

      {tab === 'inactive' && (
        <>
          <Section
            title="Pararam de treinar"
            count={stopped.length}
            note={`Têm treinos ou presenças, mas o último foi há mais de ${threshold} ${threshold === 1 ? 'semana' : 'semanas'}.`}
            empty={`Ninguém parou de treinar há ${threshold} ${threshold === 1 ? 'semana' : 'semanas'}.`}
          >
            {stopped.map((m) => (
              <MemberCard key={m.userId} member={m} onMessage={openMessage} />
            ))}
          </Section>

          <Section
            title="Nunca treinaram"
            count={never.length}
            note="Contas sem um único treino concluído nem check-in. Não é o mesmo que ter parado — pode ser um registo que nunca chegou a começar."
            empty="Todas as contas ativas já têm pelo menos um treino ou presença."
          >
            {never.map((m) => (
              <MemberCard key={m.userId} member={m} onMessage={openMessage} never />
            ))}
          </Section>
        </>
      )}

      {tab === 'week' && (
        <Weekly weekly={weekly} onEmail={sendReport} sending={sending} onOpenInactive={() => setTab('inactive')} />
      )}

      {target && (
        <SmsDialog
          open
          onClose={() => setTarget(null)}
          member={target.member}
          initialMessage={target.message}
        />
      )}
    </div>
  );
}

function Stat({ label, value, hint, tone }) {
  return (
    <div style={S.stat}>
      <span style={S.statLabel}>{label}</span>
      <strong style={{ ...S.statValue, ...(tone === 'warn' ? S.statWarn : {}) }}>{value}</strong>
      {hint && <span style={S.statHint}>{hint}</span>}
    </div>
  );
}

function Section({ title, count, note, empty, children }) {
  const items = React.Children.toArray(children);
  return (
    <section style={S.section} aria-label={title}>
      <div style={S.sectionHead}>
        <h2 style={S.h2}>{title}</h2>
        <span style={S.badge}>{count}</span>
      </div>
      <p style={S.dim}>{note}</p>
      {items.length === 0 ? <div style={S.empty}>{empty}</div> : <div style={S.cards}>{items}</div>}
    </section>
  );
}

function MemberCard({ member, onMessage, never }) {
  return (
    <article style={S.member} data-testid="inactive-member">
      <div style={S.memberTop}>
        <strong style={S.name}>{member.name || '(sem nome)'}</strong>
        {never
          ? <span style={S.chipNever}>nunca treinou</span>
          : <span style={S.chipStopped}>{sinceText(member.daysSince)}</span>}
      </div>

      {never ? (
        <p style={S.meta}>
          Registo {sinceText(member.daysSinceRegistration)}
          {member.phone ? ` · ${member.phone}` : ' · sem telefone no registo'}
        </p>
      ) : (
        <p style={S.meta}>
          {SIGNAL_LABEL[member.lastSignal] || 'Última actividade'}: {fmtDate(member.lastActivityAt)} · {sinceText(member.daysSince)}
          {member.phone ? ` · ${member.phone}` : ' · sem telefone no registo'}
        </p>
      )}

      <button type="button" style={S.action} onClick={() => onMessage(member)}>
        Enviar mensagem
      </button>
    </article>
  );
}

function Weekly({ weekly, onEmail, sending, onOpenInactive }) {
  if (!weekly) return <div style={S.empty}>Ainda não há relatório desta semana.</div>;

  const from = fmtDate(weekly.weekStart);
  const to = fmtDate(new Date(new Date(weekly.weekEnd).getTime() - 24 * 3600 * 1000));
  const nothing = weekly.workoutsCompleted === 0 && weekly.setsCompleted === 0;

  return (
    <section style={S.section} aria-label="Relatório da semana">
      <div style={S.sectionHead}>
        <h2 style={S.h2}>Semana de {from} a {to}</h2>
      </div>

      {nothing ? (
        <div style={S.empty} data-testid="week-empty">
          Esta semana ainda não há treinos concluídos. Não há números para mostrar — não os inventamos.
        </div>
      ) : (
        <div style={S.stats}>
          <Stat label="Treinos concluídos" value={weekly.workoutsCompleted} />
          <Stat label="Séries registadas" value={weekly.setsCompleted} />
          <Stat
            label="Volume"
            value={weekly.hasVolume ? `${Math.round(weekly.volumeKg).toLocaleString('pt-PT')} kg` : '—'}
            hint={weekly.hasVolume ? undefined : 'sem carga registada'}
          />
          <Stat label="Sócios que treinaram" value={weekly.membersTrained} hint={`de ${weekly.totalActiveMembers}`} />
          <Stat label="Sócios sem treino" value={weekly.membersNotTrained} tone={weekly.membersNotTrained > 0 ? 'warn' : undefined} />
          <Stat label="Novos registos" value={weekly.newRegistrations} />
        </div>
      )}

      <div style={S.inactiveBox}>
        <strong style={S.h3}>
          Inativos: {weekly.inactive?.inactiveCount ?? 0} pararam · {weekly.inactive?.neverTrainedCount ?? 0} nunca treinaram
        </strong>
        <p style={S.dim}>
          Limiar do ginásio: {weekly.inactive?.thresholdWeeks ?? 4} semanas sem treinar.
        </p>
        <button type="button" style={S.secondary} onClick={onOpenInactive}>Ver a lista</button>
      </div>

      {weekly.emailAvailable ? (
        <button type="button" style={S.primary} onClick={onEmail} disabled={sending}>
          {sending ? 'A enviar…' : 'Enviar-me este relatório por email'}
        </button>
      ) : (
        <p style={S.dim} data-testid="email-off">
          O envio por email não está configurado neste ginásio — o relatório fica neste ecrã.
        </p>
      )}
    </section>
  );
}

const card = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-lg, 14px)',
};

const S = {
  wrap: { padding: 16, maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', justifyContent: 'space-between' },
  h1: { margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' },
  h2: { margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' },
  h3: { fontSize: 15, color: 'var(--text-primary)' },
  dim: { margin: '6px 0 0', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.45 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 },
  threshold: { display: 'flex', flexDirection: 'column', minWidth: 180 },
  select: { minHeight: 44, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)', fontSize: 15 },
  rule: { margin: '14px 0 0', fontSize: 14, color: 'var(--text-primary)', ...card, padding: 12 },
  signals: { margin: '6px 0 0', fontSize: 13, color: 'var(--text-muted)' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, margin: '14px 0' },
  stat: { ...card, padding: 12, display: 'flex', flexDirection: 'column', gap: 2 },
  statLabel: { fontSize: 13, color: 'var(--text-muted)' },
  statValue: { fontSize: 24, lineHeight: 1.2, color: 'var(--text-primary)' },
  statWarn: { color: 'var(--warning-text, #b45309)' },
  statHint: { fontSize: 12, color: 'var(--text-muted)' },
  tabs: { display: 'flex', gap: 8, margin: '4px 0 12px', flexWrap: 'wrap' },
  tab: { minHeight: 44, padding: '10px 14px', borderRadius: 999, border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' },
  tabOn: { borderColor: 'var(--accent-primary)', background: 'var(--accent-primary)', color: 'var(--text-inverse)' },
  section: { ...card, padding: 14, marginBottom: 14 },
  sectionHead: { display: 'flex', alignItems: 'center', gap: 10 },
  badge: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 28, minHeight: 24, padding: '0 8px', borderRadius: 999, background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)', fontSize: 13, fontWeight: 700 },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10, marginTop: 12 },
  member: { border: '1px solid var(--border-color)', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--bg-card)' },
  memberTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' },
  name: { fontSize: 15, color: 'var(--text-primary)' },
  chipNever: { fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: 'var(--error-bg, #fee2e2)', color: 'var(--error-text, #991b1b)' },
  chipStopped: { fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' },
  meta: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },
  action: { minHeight: 44, padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-hover, var(--bg-card))', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  primary: { minHeight: 44, padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--accent-primary)', color: 'var(--text-inverse)', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  secondary: { minHeight: 44, padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' },
  empty: { ...card, padding: 16, fontSize: 14, color: 'var(--text-muted)', background: 'var(--bg-hover, var(--bg-card))' },
  inactiveBox: { ...card, padding: 12, margin: '12px 0', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' },
};
